import { useScrollReveal } from '@/hooks/useScrollReveal';

const carouselItems = [
  {
    title: '深海触手文明：深渊之下',
    category: '深度专题',
    desc: '探索触手漫画中最具想象力的深海世界观构建，从克苏鲁神话到现代科幻演绎。',
    image: 'https://readdy.ai/api/search-image?query=Deep%20ocean%20abyss%20with%20mysterious%20ancient%20ruins%20and%20organic%20flowing%20forms%2C%20dark%20crimson%20and%20teal%20atmosphere%2C%20cinematic%20underwater%20scene%2C%20epic%20wide%20shot%2C%20no%20text&width=1000&height=500&seq=carousel-01&orientation=landscape',
  },
  {
    title: '触手战斗美学：视觉盛宴',
    category: '画风解析',
    desc: '深入解析触手漫画中战斗场景的绘画技法，从线条到分镜的艺术魅力。',
    image: 'https://readdy.ai/api/search-image?query=Dynamic%20flowing%20combat%20scene%20with%20dark%20crimson%20energy%20and%20onyx%20smoke%2C%20dramatic%20action%20composition%2C%20intense%20cinematic%20lighting%2C%20wide%20shot%2C%20no%20text&width=1000&height=500&seq=carousel-02&orientation=landscape',
  },
  {
    title: '异种共生：超越人类想象',
    category: '世界观解读',
    desc: '触手漫画中的共生关系设定，带你看懂作者藏在画面中的哲学隐喻。',
    image: 'https://readdy.ai/api/search-image?query=Organic%20symbiotic%20forms%20with%20dark%20ethereal%20atmosphere%2C%20crimson%20and%20onyx%20flowing%20shapes%2C%20mysterious%20biological%20textures%2C%20cinematic%20wide%20composition%2C%20no%20text&width=1000&height=500&seq=carousel-03&orientation=landscape',
  },
  {
    title: '从经典到新锐：触手漫画简史',
    category: '文化漫谈',
    desc: '回顾触手题材漫画的发展历程，从早期经典作品到当下热门连载。',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20timeline%20of%20dark%20art%20evolution%2C%20crimson%20and%20gold%20accents%2C%20classic%20to%20modern%20transition%2C%20atmospheric%20cinematic%20scene%2C%20no%20text&width=1000&height=500&seq=carousel-04&orientation=landscape',
  },
  {
    title: '女性向触手漫画：另一种浪漫',
    category: '读者洞察',
    desc: '触手漫画不只有热血战斗，女性向触手题材的细腻情感同样精彩纷呈。',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20flowing%20organic%20forms%20with%20soft%20crimson%20and%20rose%20gold%20tones%2C%20romantic%20dark%20atmosphere%2C%20delicate%20aesthetic%2C%20cinematic%20wide%20shot%2C%20no%20text&width=1000&height=500&seq=carousel-05&orientation=landscape',
  },
  {
    title: '触手漫画改编大盘点',
    category: '跨媒体追踪',
    desc: '那些被改编成动画、游戏的触手漫画作品，IP价值一窥究竟。',
    image: 'https://readdy.ai/api/search-image?query=Media%20adaptation%20collage%20with%20dark%20crimson%20and%20onyx%20aesthetic%2C%20cinematic%20atmosphere%2C%20premium%20composition%2C%20wide%20shot%2C%20no%20text&width=1000&height=500&seq=carousel-06&orientation=landscape',
  },
];

export default function CarouselSection() {
  const { ref: titleRef, visible: titleVisible } = useScrollReveal(0.12);

  return (
    <section id="carousel" className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div ref={titleRef} className={`text-center mb-12 ${titleVisible ? 'animate-fade-in-up' : 'animate-initial-hidden'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100/60 border border-primary-200/50 mb-4">
            <i className="ri-compass-3-line text-primary-600 text-xs w-4 h-4 flex items-center justify-center"></i>
            <span className="text-xs font-medium text-primary-700 whitespace-nowrap">深度专题 · 探索发现</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            焦点专题
          </h2>
          <div className="w-16 h-0.5 bg-primary-500 mx-auto mb-4"></div>
          <p className="text-sm md:text-base text-foreground-600">
            触手漫画深度内容，发现作品背后的故事与精彩
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {carouselItems.map((item, idx) => (
            <CarouselCard key={idx} item={item} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CarouselCard({ item, idx }: { item: typeof carouselItems[0]; idx: number }) {
  const { ref, visible } = useScrollReveal(0.06);

  return (
    <a
      ref={ref}
      href="#cards"
      className={`group block bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-300 transition-all overflow-hidden cursor-pointer ${
        visible ? `animate-fade-in-up animate-stagger-${idx + 1}` : 'animate-initial-hidden'
      } ${idx < 2 ? 'lg:col-span-3 md:col-span-2' : ''}`}
    >
      <div className={`relative overflow-hidden ${idx < 2 ? 'h-52 md:h-64' : 'h-40 md:h-48'}`}>
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-primary-500/80 text-white text-xs font-medium rounded whitespace-nowrap">
            {item.category}
          </span>
        </div>
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="text-sm md:text-base font-semibold text-white line-clamp-1">{item.title}</h3>
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs md:text-sm text-foreground-600 line-clamp-2">{item.desc}</p>
        <div className="flex items-center gap-1 mt-3 text-xs text-primary-600 font-medium">
          阅读专题
          <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
        </div>
      </div>
    </a>
  );
}